import React, { useState } from "react";
import axios from "axios";
import { ArrowRight, Check } from "lucide-react";
import { useSiteContent } from "../hooks/useSiteContent";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function EmailSignup() {
  const c = useSiteContent();
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle");
  const [message, setMessage] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setStatus("error");
      setMessage("Please share a valid email address.");
      return;
    }
    setStatus("loading");
    try {
      await axios.post(`${API}/subscribe`, { email });
      setStatus("success");
      setMessage(c.signup_success || "You're in. Look for our first letter soon.");
      setEmail("");
    } catch (err) {
      setStatus("error");
      setMessage(
        err?.response?.data?.detail
          ? String(err.response.data.detail)
          : "Something went quiet. Please try again."
      );
    }
  };

  return (
    <section
      id="signup"
      data-testid="email-signup-section"
      className="relative bg-background"
    >
      <div className="max-w-3xl mx-auto px-6 sm:px-8 lg:px-12 py-24 sm:py-32 text-center">
        <p className="text-xs uppercase tracking-[0.3em] text-primary mb-6 fade-up">
          {c.signup_overline || "The Journal"}
        </p>
        <h2
          data-testid="signup-heading"
          className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light leading-[1.05] tracking-tight fade-up fade-up-delay-1"
        >
          {c.signup_heading || "Join the GentleGiTo Journal"}
        </h2>
        <p className="mt-6 text-base sm:text-lg text-muted-foreground leading-relaxed max-w-xl mx-auto fade-up fade-up-delay-2">
          {c.signup_description ||
            "A quiet letter, once or twice a month. Reflections, small rituals, and gentle guidance for modern grandparents — never noise."}
        </p>

        <form
          onSubmit={handleSubmit}
          data-testid="signup-form"
          className="mt-12 max-w-md mx-auto flex items-end gap-4 fade-up fade-up-delay-3"
        >
          <div className="flex-1 text-left">
            <label
              htmlFor="signup-email"
              className="text-xs uppercase tracking-[0.2em] text-muted-foreground"
            >
              Email address
            </label>
            <input
              id="signup-email"
              data-testid="signup-email-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
              className="quiet-input mt-2"
              disabled={status === "loading"}
            />
          </div>
          <button
            data-testid="signup-submit-button"
            type="submit"
            disabled={status === "loading"}
            className="group pb-3 pr-1 inline-flex items-center gap-2 text-sm tracking-wide text-foreground hover:text-primary transition-colors duration-300 disabled:opacity-60"
          >
            {status === "success" ? (
              <>
                Joined <Check size={16} strokeWidth={1.5} />
              </>
            ) : (
              <>
                {c.signup_button || "Join"}{" "}
                <ArrowRight
                  size={16}
                  strokeWidth={1.5}
                  className="transition-transform duration-300 group-hover:translate-x-1"
                />
              </>
            )}
          </button>
        </form>

        {message && (
          <p
            data-testid="signup-message"
            className={`mt-6 text-sm italic font-serif ${
              status === "error" ? "text-destructive" : "text-primary"
            }`}
          >
            {message}
          </p>
        )}
      </div>
    </section>
  );
}
